import { connect } from 'react-redux';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button,
  Checkbox,
  Input,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  Spinner,
  ProgressBar,
  Badge,
  Textarea,
} from '@fluentui/react-components';
import {
  ArrowClockwise20Regular,
  CheckmarkCircle24Filled,
  ArrowSyncDismiss24Filled,
  DocumentArrowUp20Regular,
  SyncOff16Regular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { FontIcon } from '@fluentui/react';

import * as ComponentFunctions from './Contract.IAImport.Functions';
import { ContractIAImportProcess } from '../../../models/Entities/Contracts/ContractIAImportProcess';
import FileSelectorComponent from '../../../commons/component/Inputs/FileSelector/FileSelector.Component';
import ContractReviewLauncher from '../ContractReview/ContractReviewLauncher/ContractReviewLauncher.Connected';
import ContractReviewStatus from '../ContractReview/ContractReviewStatus/ContractReviewStatus.Component';
import { errorText, getImportAvailability, getParametrizationState, isReviewableAddendum } from '../ContractReview/ContractReview.Functions';
import ContractIAImportFeedbackComponent from './Contract.IAImport.Feedback.Component';
import { GuideDrawer, GuideDrawerForm, GuideDrawerSection } from '../../../theme/components/GuideDrawer';
import { GuideField, GuideHelpText } from '../../../theme/components/GuideField';
import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import './Contract.IAImport.Styles.css';

// Status: 1 = registered/pending, 2 = inProgress, 3 = finished, 4 = cancelled, 5 = fail
type StatusVariant = 'idle' | 'running' | 'success' | 'cancelled' | 'error';

function getStatusVariant(status?: number): StatusVariant {
  switch (status) {
    case 1:
    case 2:
      return 'running';
    case 3:
      return 'success';
    case 4:
      return 'cancelled';
    case 5:
      return 'error';
    default:
      return 'idle';
  }
}

const StatusIcon = ({ status, isLoading }: { status?: number; isLoading?: boolean }) => {
  if (isLoading) return <Spinner size="extra-tiny" />;
  switch (status) {
    case 1:
    case 2:
      return <Spinner size="extra-tiny" />;
    case 3:
      return <CheckmarkCircle24Filled className="ia-import-status__icon--success" />;
    case 4:
      return <SyncOff16Regular className="ia-import-status__icon--cancelled" />;
    case 5:
      return <ArrowSyncDismiss24Filled className="ia-import-status__icon--error" />;
    default:
      return <FontIcon iconName="CloudUpload" className="ia-import-status__icon--idle" />;
  }
};

const ContractIAImportComponent = (props: ComponentFunctions.Props) => {
  const { contractId, lastProcess } = props;
  const { t } = useTranslation();

  const [fileIds, setFileIds] = useState<string[]>([]);
  const [hasPassword, setHasPassword] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [comments, setComments] = useState<string>('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const lastProcessIdRef = useRef<string | undefined>(undefined);

  // When IA import finishes, refresh addendums/stages/concepts
  ComponentFunctions.useIAImportCompletionRefresh(
    contractId,
    lastProcess,
    props.GetAllContractStagesByAddendumId,
    props.GetAllContractsConceptByAddendum,
    props.GetContractAddendumsByContractId,
    () => props.GetContractParametrizationStatus(contractId)
  );

  // El estado de parametrización se pide al montar y al terminar la importación. Contracts 021 §2: con el contrato
  // cargado se reimporta sobre una adenda nueva mientras no haya liquidaciones ni adendas sin vigencia.
  const parametrizationState = getParametrizationState(props.parametrization);
  const importAvailability = getImportAvailability(props.parametrization);
  const isReimport = parametrizationState === 'parameterized';
  // Una instancia de negociación no se revisa con IA (DEC-007); sin adenda seleccionada se revisa todo el contrato.
  const reviewableAddendum = !props.addendum || isReviewableAddendum(props.addendum);
  useEffect(() => {
    if (contractId) props.GetContractParametrizationStatus(contractId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractId]);

  // Refetch whenever the contract changes. The old guard also required
  // lastProcess to be null, so switching contracts never refetched: the previous
  // contract's process was non-null and left the widget frozen on stale data.
  useEffect(() => {
    if (contractId) {
      props.GetLastImportContractProcess(contractId);
    }
  }, [contractId]);

  // Poll every 5s while status is 1 or 2
  useEffect(() => {
    const isActiveStatus = lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!isActiveStatus) return;
    const interval = setInterval(() => {
      props.GetLastImportContractProcess(contractId);
    }, 5000);
    return () => clearInterval(interval);
  }, [lastProcess?.status, contractId]);

  // Fetch once after successful creation
  useEffect(() => {
    if (props.successCreating) {
      props.GetLastImportContractProcess(contractId);
      setOpenDrawer(false);
    }
  }, [props.successCreating]);

  // 409: el contrato no se puede (re)importar ahora (liquidaciones o adenda sin vigencia) → se cierra el formulario y se
  // refresca el estado, que trae el motivo. 400 y 503 dejan el formulario abierto para corregir o reintentar.
  const createErrorCode = props.failureCreating ? props.errorCreating?.ErrorCode : undefined;
  useEffect(() => {
    if (createErrorCode === 409) {
      setOpenDrawer(false);
      props.GetContractParametrizationStatus(contractId);
      props.GetContractAddendumsByContractId(contractId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createErrorCode, props.errorCreating]);

  // Elapsed time counter while running
  useEffect(() => {
    const active = !!lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
    if (!active) {
      setElapsedTime(0);
      lastProcessIdRef.current = undefined;
      return;
    }
    if (lastProcess?.id && lastProcessIdRef.current !== lastProcess.id) {
      lastProcessIdRef.current = lastProcess.id;
    }

    const calculateElapsed = () => {
      if (lastProcess.startTimestamp) {
        let ts = lastProcess.startTimestamp as string;
        if (typeof ts === 'string') {
          ts = ts.replace(/(\.\d{3})\d+/, '$1');
          if (!ts.endsWith('Z')) ts += 'Z';
        }
        const elapsed = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
        return elapsed > 0 ? elapsed : 0;
      }
      return 0;
    };

    setElapsedTime(calculateElapsed());
    const timer = setInterval(() => setElapsedTime(calculateElapsed()), 1000);
    return () => clearInterval(timer);
  }, [lastProcess?.id, lastProcess?.status, lastProcess?.startTimestamp]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDuration = (durationString?: string): string => {
    if (!durationString) return '—';
    const parts = durationString.split(':');
    if (parts.length >= 2) {
      const h = parseInt(parts[0] || '0', 10);
      const m = parseInt(parts[1] || '0', 10);
      const s = parseInt(parts[2] || '0', 10);
      return formatTime(h * 3600 + m * 60 + s);
    }
    const total = parseInt(durationString, 10);
    return isNaN(total) ? '—' : formatTime(total);
  };

  const formatDateTime = (raw?: Date | string | null): string | null => {
    if (!raw) return null;
    return new Date(raw as string).toLocaleString(undefined, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const isRunning = !!lastProcess && (lastProcess.status === 1 || lastProcess.status === 2);
  const isDone = !!lastProcess && (lastProcess.status === 3 || lastProcess.status === 4 || lastProcess.status === 5);
  const statusVariant = getStatusVariant(lastProcess?.status);
  const isInitialLoading = props.isGettingLast && lastProcess == null;

  const canStart = useMemo(
    () => !!contractId && fileIds.length > 0 && !props.isCreating,
    [contractId, fileIds, props.isCreating]
  );

  const statusLabel = (variant: StatusVariant): string =>
    variant === 'running' && lastProcess?.status === 1 ? t('contractIAImport.state.starting') : t(`contractIAImport.state.${variant}`);

  const lastTimestamp = lastProcess && (lastProcess.endTimestamp || lastProcess.dateTimeSync || lastProcess.startTimestamp);
  const lastDateFormatted = formatDateTime(lastTimestamp as string | null);

  // Contracts 021 §1: la reimportación deja una adenda nueva sin vigencia; se avisa hasta que le carguen el inicio.
  const createdAddendum =
    lastProcess?.status === 3 && lastProcess.contractAddendumId
      ? props.contract?.addendums?.find(a => a.id === lastProcess.contractAddendumId)
      : undefined;
  const createdWithoutValidity = !!createdAddendum && !createdAddendum.contractAddendumExpiryStartDate;

  const onOpenForm = () => {
    setOpenDrawer(true);
    props.GetImportContractProcessHistory(contractId);
  };
  const onCloseForm = () => setOpenDrawer(false);
  const onStartImport = () => {
    props.CreateImportContractProcess({
      contractID: contractId,
      fileIds,
      hasPassword: !!hasPassword,
      reWriteExistingData: false,
      password: hasPassword ? password : undefined,
      additionalPromptInformation: comments?.trim() || undefined,
    });
  };

  const onLocalFilesChange = (files: any[]) => {
    setFileIds(files?.map(file => String(file.fileId || file.id)).filter(Boolean) || []);
  };

  const startLabel = props.isCreating
    ? t('contractIAImport.widget.starting')
    : isReimport
      ? t('contractIAImport.reimport.start')
      : t('contractIAImport.actions.start');

  return (
    <div className="ia-import-widget">

      {/* ── Header ── */}
      <div className="ia-import-widget__header">
        <div className="ia-import-widget__header-left">
          <FontIcon iconName="CloudImport" className="ia-import-widget__header-icon" />
          <span className="ia-import-widget__title">{t('contractIAImport.widget.title')}</span>
          <Badge appearance="filled" color="warning" size="small">{t('contractIAImport.widget.beta')}</Badge>
        </div>
        <button
          type="button"
          className="ia-import-widget__refresh-btn"
          title={t('contractIAImport.actions.refresh')}
          aria-label={t('contractIAImport.actions.refresh')}
          disabled={props.isGettingLast}
          onClick={() => props.GetLastImportContractProcess(contractId)}
        >
          <FontIcon iconName="Refresh" />
        </button>
      </div>

      {/* ── Status ── */}
      <div className={`ia-import-status ia-import-status--${statusVariant}`}>
        <div className="ia-import-status__icon-col">
          <StatusIcon status={lastProcess?.status} isLoading={isInitialLoading} />
        </div>
        <div className="ia-import-status__content">
          {isInitialLoading ? (
            <span className="ia-import-status__label">{t('contractIAImport.widget.checking')}</span>
          ) : (
            <>
              <span className="ia-import-status__label">{statusLabel(statusVariant)}</span>

              {isRunning && (
                <div className="ia-import-metrics">
                  <div className="ia-import-metric">
                    <span className="ia-import-metric__label">{t('contractIAImport.widget.elapsed')}</span>
                    <span className="ia-import-metric__value">
                      <FontIcon iconName="Clock" />
                      {formatTime(elapsedTime)}
                    </span>
                  </div>
                </div>
              )}

              {isDone && (
                <div className="ia-import-metrics">
                  {lastDateFormatted && (
                    <div className="ia-import-metric">
                      <span className="ia-import-metric__label">{t('contractIAImport.widget.lastRun')}</span>
                      <span className="ia-import-metric__value">{lastDateFormatted}</span>
                    </div>
                  )}
                  {lastProcess?.duration && (
                    <div className="ia-import-metric">
                      <span className="ia-import-metric__label">{t('contractIAImport.widget.duration')}</span>
                      <span className="ia-import-metric__value">{formatDuration(lastProcess.duration)}</span>
                    </div>
                  )}
                </div>
              )}

              {statusVariant === 'idle' && (
                <span className="ia-import-status__hint">{t('contractIAImport.widget.idleHint')}</span>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Progress bar while running ── */}
      {isRunning && (
        <div className="ia-import-widget__progress">
          <ProgressBar />
        </div>
      )}

      {/* ── Process error detail ── */}
      {lastProcess?.status === 5 && lastProcess.errorMessage && (
        <div className="ia-import-widget__process-error">
          <FontIcon iconName="ErrorBadge" />
          <span>{lastProcess.errorMessage}</span>
        </div>
      )}

      {/* ── Adenda creada sin vigencia (Contracts 021 §1) ── */}
      {createdWithoutValidity && (
        <div className="ia-import-widget__actions">
          <GuideMessageBar intent="info" data-testid="ia-import-created-addendum">
            <MessageBarBody>{t('contractIAImport.reimport.created', { name: createdAddendum!.contractAddendumName })}</MessageBarBody>
          </GuideMessageBar>
        </div>
      )}

      {/* ── Feedback de la importación ── */}
      {/* Va después del estado y antes del botón: la puntuación pertenece a la
          ejecución que se acaba de leer, no a la próxima que se va a lanzar. */}
      {isDone && <ContractIAImportFeedbackComponent process={lastProcess} />}

      {/* ── Action button ── */}
      {!isRunning && parametrizationState === 'empty' && (
        <div className="ia-import-widget__actions">
          <Button
            appearance="primary"
            className="ia-import-widget__cta"
            icon={<FontIcon iconName="CloudUpload" />}
            onClick={onOpenForm}
            disabled={props.isCreating}
          >
            {props.isCreating
              ? t('contractIAImport.widget.starting')
              : (isDone ? t('contractIAImport.widget.newImport') : t('contractIAImport.actions.openForm'))}
          </Button>
        </div>
      )}
      {!isRunning && parametrizationState === 'parameterized' && (
        <div className="ia-import-widget__actions ia-import-widget__review" data-testid="ia-import-review-actions">
          <span className="ia-import-status__hint">{t('contractIAImport.parametrization.parameterized')}</span>
          {/* Contrato ya cargado: cada botón abre su drawer con las opciones y las revisiones anteriores. */}
          <div className="ia-import-widget__review-buttons">
            {reviewableAddendum && <ContractReviewLauncher mode="Review" contractId={contractId} addendum={props.addendum} />}
            <ContractReviewLauncher mode="NewAddendum" contractId={contractId} />
          </div>
          {!reviewableAddendum && <span className="ia-import-status__hint">{t('contractReview.panel.negotiation')}</span>}
          <ContractReviewStatus contractId={contractId} addendum={props.addendum} />

          {/* ── Reimportar sobre una adenda nueva (Contracts 021 §1 y §2) ── */}
          <div className="ia-import-widget__reimport" data-testid="ia-import-reimport">
            <span className="ia-import-widget__section-title">{t('contractIAImport.reimport.title')}</span>
            {importAvailability === 'blocked' ? (
              <GuideMessageBar intent="warning" data-testid="ia-import-blocked">
                <MessageBarBody>
                  <MessageBarTitle>{t('contractIAImport.reimport.blockedTitle')}</MessageBarTitle>
                  {props.parametrization?.importBlockedReason ?? t('contractIAImport.reimport.blockedFallback')}
                </MessageBarBody>
                <MessageBarActions>
                  <Button
                    size="small"
                    icon={<ArrowClockwise20Regular />}
                    disabled={props.parametrization?.isLoading}
                    onClick={() => props.GetContractParametrizationStatus(contractId)}
                  >
                    {t('contractIAImport.reimport.recheck')}
                  </Button>
                </MessageBarActions>
              </GuideMessageBar>
            ) : (
              <>
                <span className="ia-import-status__hint">{t('contractIAImport.reimport.hint')}</span>
                <Button
                  appearance="secondary"
                  icon={<DocumentArrowUp20Regular />}
                  onClick={onOpenForm}
                  disabled={props.isCreating}
                  data-testid="ia-import-reimport-open"
                >
                  {t('contractIAImport.reimport.open')}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
      {!isRunning && parametrizationState === 'unknown' && (
        <div className="ia-import-widget__actions">
          {props.parametrization?.error ? (
            <MessageBar intent="warning">
              <MessageBarBody>{t('contractIAImport.parametrization.failed')}</MessageBarBody>
              <Button size="small" onClick={() => props.GetContractParametrizationStatus(contractId)}>
                {t('contractIAImport.actions.refresh')}
              </Button>
            </MessageBar>
          ) : (
            <Spinner size="tiny" label={t('contractIAImport.parametrization.checking')} />
          )}
        </div>
      )}

      {/* ── API errors ── */}
      {props.failureCreating && createErrorCode === 409 && (
        <MessageBar intent="warning" className="ia-import-widget__api-error" role="alert">
          <MessageBarBody>
            <MessageBarTitle>{t('contractIAImport.reimport.blockedTitle')}</MessageBarTitle>
            {errorText(props.errorCreating) ?? t('contractIAImport.reimport.blockedFallback')}
          </MessageBarBody>
        </MessageBar>
      )}
      {((props.failureCreating && createErrorCode !== 409 && createErrorCode !== 400 && createErrorCode !== 503) || props.failureGettingLast) && (
        <MessageBar intent="error" className="ia-import-widget__api-error">
          <MessageBarBody>
            <MessageBarTitle>{t('contractIAImport.error.title')}</MessageBarTitle>
            {props.errorCreating?.ErrorMessage || props.errorGettingLast?.ErrorMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* ── Beta footer ── */}
      <div className="ia-import-widget__beta-footer">
        <FontIcon iconName="TestBeaker" className="ia-import-widget__beta-footer-icon" />
        <span>{t('contractIAImport.widget.betaFooter')}</span>
      </div>

      {/* ── Import Drawer ── */}
      <GuideDrawer
        open={openDrawer}
        size="medium"
        eyebrow={props.contract?.contractNumber || undefined}
        title={isReimport ? t('contractIAImport.reimport.formTitle') : t('contractIAImport.form.title')}
        subtitle={isReimport ? t('contractIAImport.reimport.formSubtitle') : t('contractIAImport.drawer.subtitle')}
        onDismiss={onCloseForm}
        preventDismiss={props.isCreating}
        testId="ia-import-drawer"
        footer={
          <div className="ia-import-form__footer">
            <div className="ia-import-form__footer-actions">
              <Button appearance="secondary" onClick={onCloseForm} data-drawer-action="secondary">
                {t('contractIAImport.actions.cancel')}
              </Button>
              <Button appearance="primary" disabled={!canStart} onClick={onStartImport} data-drawer-action="primary">
                {startLabel}
              </Button>
            </div>
          </div>
        }
      >
        <GuideDrawerForm autoComplete="off" testId="ia-import-form">
          {/* 400 (pedido inválido) y 503 (Settlements no respondió): el formulario queda abierto con el motivo */}
          {props.failureCreating && createErrorCode === 400 && (
            <GuideMessageBar intent="error" role="alert">
              <MessageBarBody>{errorText(props.errorCreating) ?? t('contractIAImport.error.title')}</MessageBarBody>
            </GuideMessageBar>
          )}
          {props.failureCreating && createErrorCode === 503 && (
            <GuideMessageBar intent="warning" role="alert" data-testid="ia-import-unavailable">
              <MessageBarBody>
                <MessageBarTitle>{t('contractIAImport.error.unavailableTitle')}</MessageBarTitle>
                {errorText(props.errorCreating) ?? t('contractIAImport.error.unavailable')}
              </MessageBarBody>
            </GuideMessageBar>
          )}

          {/* ── Qué va a pasar (reimportación) ── */}
          {isReimport && (
            <GuideMessageBar intent="info" data-testid="ia-import-reimport-steps">
              <MessageBarBody>
                <MessageBarTitle>{t('contractIAImport.reimport.stepsTitle')}</MessageBarTitle>
                <ol className="ia-import-steps">
                  <li>{t('contractIAImport.reimport.step1')}</li>
                  <li>{t('contractIAImport.reimport.step2')}</li>
                  <li>{t('contractIAImport.reimport.step3')}</li>
                </ol>
              </MessageBarBody>
            </GuideMessageBar>
          )}

          {/* ── Archivos ── */}
          <GuideDrawerSection
            title={t('contractIAImport.drawer.filesTitle')}
            description={t('contractIAImport.drawer.filesDescription')}
            actions={fileIds.length > 0 ? <span className="ia-import-card__count">{fileIds.length}</span> : undefined}
          >
            <FileSelectorComponent contractId={contractId} multiple={true} onChange={onLocalFilesChange} />
            {fileIds.length === 0 && (
              <GuideHelpText>
                {t('contractIAImport.form.fileId.hint')}
              </GuideHelpText>
            )}
          </GuideDrawerSection>

          {/* ── Opciones ── */}
          <GuideDrawerSection
            title={t('contractIAImport.drawer.optionsTitle')}
            description={t('contractIAImport.drawer.optionsDescription')}
          >
            <Checkbox
              checked={hasPassword}
              onChange={(_, d) => setHasPassword(!!d.checked)}
              label={t('contractIAImport.form.passwordProtected')}
            />
            {hasPassword && (
              <GuideField label={t('contractIAImport.form.password')}>
                <Input
                  type="password"
                  value={password}
                  onChange={(_, d) => setPassword(d.value)}
                  name="contract-file-password"
                  autoComplete="new-password"
                />
              </GuideField>
            )}
          </GuideDrawerSection>

          {/* ── Instrucciones para la IA: también las usa la revisión automática (Contracts 021, DEC-027) ── */}
          <GuideDrawerSection
            title={t('contractIAImport.drawer.instructionsTitle')}
            description={t('contractIAImport.drawer.instructionsDescription')}
          >
            {/* A textarea, not a one-line input: this is free-form guidance
                for the model and was being typed into a field that hid it. */}
            <Textarea
              rows={3}
              resize="vertical"
              value={comments}
              onChange={(_, d) => setComments(d.value)}
              name="contract-file-comments"
              autoComplete="off"
              aria-label={t('contractIAImport.drawer.instructionsTitle')}
              placeholder={t('contractIAImport.drawer.instructionsPlaceholder')}
            />
          </GuideDrawerSection>

          {/* ── Historial ── */}
          <GuideDrawerSection
            title={t('contractIAImport.drawer.historyTitle')}
            description={t('contractIAImport.drawer.historyDescription')}
            actions={props.history.length > 0 ? <span className="ia-import-card__count">{props.history.length}</span> : undefined}
          >
            {props.isGettingHistory && (
              <div className="ia-import-history__loading">
                <Spinner size="extra-tiny" />
                <span>{t('contractIAImport.drawer.historyLoading')}</span>
              </div>
            )}

            {!props.isGettingHistory && props.history.length === 0 && (
              <p className="ia-import-history__empty">{t('contractIAImport.drawer.historyEmpty')}</p>
            )}

            {!props.isGettingHistory && props.history.length > 0 && (
              <ul className="ia-import-history">
                {props.history.map((process: ContractIAImportProcess) => {
                  const variant = getStatusVariant(process.status);
                  return (
                    <li key={process.id} className={'ia-import-history__item ia-import-history__item--' + variant}>
                      <div className="ia-import-history__row">
                        <span className={'ia-import-history__status ia-import-history__status--' + variant}>
                          {t(`contractIAImport.historyStatus.${variant}`)}
                        </span>
                        <span className="ia-import-history__date">
                          {formatDateTime(process.startTimestamp as string) || '—'}
                        </span>
                      </div>
                      <div className="ia-import-history__meta">
                        <span>{t('contractIAImport.drawer.historyDuration', { value: formatDuration(process.duration) })}</span>
                      </div>
                      {process.errorMessage && (
                        <p className="ia-import-history__error">{process.errorMessage}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </GuideDrawerSection>

        </GuideDrawerForm>
      </GuideDrawer>

    </div>
  );
};

export { ContractIAImportComponent };

export default connect(ComponentFunctions.mapStateToProps, ComponentFunctions.mapDispatchToProps)(ContractIAImportComponent as any);
